import { resolveMealPlannedAt } from "@macro-tracker/models";
import type {
  DiaryDay,
  DiaryDayBody,
  DiaryDayEntry,
  DiaryMeal,
  DiaryGoalFlexMacro,
} from "@/services/diaryDays";

export interface MacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

export const emptyTotals = (): MacroTotals => ({
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
});

const isExerciseEntry = (entry: DiaryDayEntry): boolean =>
  entry.source.type === "exercise";

/** Non-deleted meals, the basis for all totals and grouping. */
export const activeMeals = (body: DiaryDayBody): DiaryMeal[] =>
  body.meals.filter((meal) => !meal.deletedAt);

/** All entries across non-deleted meals. */
export const allEntries = (body: DiaryDayBody): DiaryDayEntry[] =>
  activeMeals(body).flatMap((meal) => meal.entries);

export const foodEntries = (body: DiaryDayBody): DiaryDayEntry[] =>
  allEntries(body).filter((entry) => !isExerciseEntry(entry));

export const exerciseEntries = (body: DiaryDayBody): DiaryDayEntry[] =>
  allEntries(body).filter(isExerciseEntry);

const accumulate = (
  target: MacroTotals,
  entry: DiaryDayEntry,
): MacroTotals => ({
  calories: target.calories + (entry.nutritionSnapshot.calories ?? 0),
  protein: target.protein + (entry.nutritionSnapshot.protein ?? 0),
  carbs: target.carbs + (entry.nutritionSnapshot.carbs ?? 0),
  fat: target.fat + (entry.nutritionSnapshot.fat ?? 0),
});

export const computeFoodTotals = (body: DiaryDayBody): MacroTotals =>
  foodEntries(body).reduce(accumulate, emptyTotals());

export const computeExerciseCaloriesBurned = (body: DiaryDayBody): number =>
  exerciseEntries(body).reduce(
    (sum, entry) => sum + (entry.nutritionSnapshot.calories ?? 0),
    0,
  );

export const computeEffectiveTotals = (body: DiaryDayBody): MacroTotals => {
  if (body.importedTotals) {
    return {
      calories: body.importedTotals.calories,
      protein: body.importedTotals.protein,
      carbs: body.importedTotals.carbs,
      fat: body.importedTotals.fat,
    };
  }
  return computeFoodTotals(body);
};

/** Food totals grouped by configured meal id (`meal.mealId`). */
export const computeMealTotals = (
  body: DiaryDayBody,
): Map<number, MacroTotals> => {
  const totals = new Map<number, MacroTotals>();
  for (const meal of activeMeals(body)) {
    if (meal.mealId === null || meal.mealId === undefined) continue;
    let current = totals.get(meal.mealId) ?? emptyTotals();
    for (const entry of meal.entries) {
      if (isExerciseEntry(entry)) continue;
      current = accumulate(current, entry);
    }
    totals.set(meal.mealId, current);
  }
  return totals;
};

/** Food totals grouped by meal id (`meal.id`). */
export const computeMealPlanTotals = (
  body: DiaryDayBody,
): Map<string, MacroTotals> => {
  const totals = new Map<string, MacroTotals>();
  for (const meal of activeMeals(body)) {
    let current = emptyTotals();
    for (const entry of meal.entries) {
      if (isExerciseEntry(entry)) continue;
      current = accumulate(current, entry);
    }
    totals.set(meal.id, current);
  }
  return totals;
};

export interface DailyGoalTargets {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  flexMacro?: DiaryGoalFlexMacro | null;
}

export const resolveDailyGoals = (
  body: DiaryDayBody,
): DailyGoalTargets | null => {
  if (body.goalSnapshot) {
    const goals: DailyGoalTargets = {
      calories: body.goalSnapshot.caloriesTarget,
      protein: body.goalSnapshot.proteinG,
      carbs: body.goalSnapshot.carbsG,
      fat: body.goalSnapshot.fatG,
    };
    if (body.goalSnapshot.flexMacro) {
      goals.flexMacro = body.goalSnapshot.flexMacro;
    }
    return goals;
  }
  return null;
};

export const computeRemaining = (
  body: DiaryDayBody,
): MacroTotals | null => {
  const goals = resolveDailyGoals(body);
  if (!goals) return null;
  const totals = computeEffectiveTotals(body);
  const exerciseBurn = computeExerciseCaloriesBurned(body);
  return {
    calories: goals.calories + exerciseBurn - totals.calories,
    protein: goals.protein - totals.protein,
    carbs: goals.carbs - totals.carbs,
    fat: goals.fat - totals.fat,
  };
};

export const computeNetCalories = (body: DiaryDayBody): number => {
  const totals = computeEffectiveTotals(body);
  return totals.calories - computeExerciseCaloriesBurned(body);
};

export const sortedEntries = (body: DiaryDayBody): DiaryDayEntry[] =>
  [...allEntries(body)].sort((a, b) => {
    const byLoggedAt = a.loggedAt.localeCompare(b.loggedAt);
    if (byLoggedAt !== 0) return byLoggedAt;
    const byCreatedAt = a.createdAt.localeCompare(b.createdAt);
    if (byCreatedAt !== 0) return byCreatedAt;
    return a.id.localeCompare(b.id);
  });

/** Non-deleted meals sorted by effective planned time. */
export const sortedMealPlans = (body: DiaryDayBody): DiaryMeal[] =>
  [...body.meals]
    .filter((meal) => !meal.deletedAt)
    .sort((a, b) => resolveMealPlannedAt(a).localeCompare(resolveMealPlannedAt(b)));

export interface DiaryDaySummary {
  date: string;
  totals: MacroTotals;
  effectiveTotals: MacroTotals;
  exerciseCaloriesBurned: number;
  goals: DailyGoalTargets | null;
  remaining: MacroTotals | null;
}

export const summarizeDay = (day: DiaryDay): DiaryDaySummary => ({
  date: day.date,
  totals: computeFoodTotals(day.body),
  effectiveTotals: computeEffectiveTotals(day.body),
  exerciseCaloriesBurned: computeExerciseCaloriesBurned(day.body),
  goals: resolveDailyGoals(day.body),
  remaining: computeRemaining(day.body),
});
